import { apiService } from "api/apiService";
import type {
  ConversationRowResource,
  ConversationInvitationResource,
  ConversationResource,
  MyStudentsAllConversationsCounts200,
  MyStudentsConversationsCounts200,
} from "api/generated";
import { mapPaginatedResponse } from "api/mappers/pagination.mapper";
import type { PaginatedResponse, PaginatedResult } from "api/types";
import {
  classroomConversationFormDataToStoreDto,
  classroomConversationInvitationFormDataToStoreDto,
  conversationRowFromDto,
  conversationFromDto,
  conversationInvitationFromDto,
} from "conversations/api/mappers";
import type {
  ClassroomConversationInvitationFormData,
  ConversationRow,
  Conversation,
  ConversationFormData,
  ConversationInvitation,
  ConversationTabCounts,
} from "conversations/types";
import { AGGREGATE_MENTOR_CLASS } from "classroom/constants";

/**
 * `all` has its own endpoints rather than a class named "all": the aggregate resolves to
 * every mentorklas of the teacher, and its gate is a different ability.
 */
const basePath = (mentorClass: string): string =>
  mentorClass === AGGREGATE_MENTOR_CLASS
    ? "api/my-students/all/conversations"
    : `api/my-students/${encodeURIComponent(mentorClass)}/conversations`;

export const fetchMentorClassConversations = async (
  mentorClass: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<PaginatedResult<ConversationRow>> => {
  const { data } = await apiService.get<
    PaginatedResponse<ConversationRowResource>
  >(basePath(mentorClass), { params, signal });

  return mapPaginatedResponse(data, conversationRowFromDto);
};

export const fetchMentorClassConversationCounts = async (
  mentorClass: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<ConversationTabCounts> => {
  const { data } = await apiService.get<
    MyStudentsConversationsCounts200 | MyStudentsAllConversationsCounts200
  >(`${basePath(mentorClass)}/counts`, { params, signal });

  return data;
};

/**
 * The gespreksverslag endpoint of this mentorklas. The class stamps the subject and checks
 * the student, so neither travels as a free choice — see
 * MEETING-CLASSROOM-CONVERSATION-SUBJECT-LOCK-001.
 */
export const createMentorClassConversation = async (
  mentorClass: string,
  formData: ConversationFormData,
): Promise<Conversation> => {
  const { data } = await apiService.post<ConversationResource>(
    basePath(mentorClass),
    classroomConversationFormDataToStoreDto(formData),
  );

  return conversationFromDto(data);
};

export const createMentorClassConversationInvitation = async (
  mentorClass: string,
  formData: ClassroomConversationInvitationFormData,
): Promise<ConversationInvitation> => {
  const { data } = await apiService.post<ConversationInvitationResource>(
    `api/my-students/${encodeURIComponent(mentorClass)}/conversation-invitations`,
    classroomConversationInvitationFormDataToStoreDto(formData),
  );

  return conversationInvitationFromDto(data);
};
